import { useCallback, useEffect, useRef, type RefObject } from "react";

// Slow enough to read as "there is more this way", not as a ticker.
const SPEED_PX_PER_SEC = 22;
// Let the entrance animation finish before anything starts moving.
const START_DELAY_MS = 2200;
// Rest at each end so the last and first cards can actually be read.
const END_PAUSE_MS = 1800;
const RETURN_MS = 1200;
// Anything that means the visitor is handling the row themselves.
const STOP_EVENTS = ["touchstart", "wheel", "pointerdown", "keydown", "focusin"] as const;

/**
 * Slowly drifts a horizontal scroller to the right and back, as a hint that it
 * holds more than fits on screen. The scroller stays a normal native scroller:
 * the first touch, wheel, key or focus inside it stops the drift for good (the
 * visitor has found it), a mouse hovering over it only pauses it. Does nothing
 * with prefers-reduced-motion or when nothing overflows.
 *
 * Returns `stop`, for controls outside the scroller (e.g. arrow buttons).
 */
export function useAutoDrift(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
): () => void {
  const stopRef = useRef<() => void>(() => {});

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let timer = 0;
    let last = 0;
    // Float accumulator: scrollLeft rounds to device pixels, so adding a
    // sub-pixel step to it directly would never move.
    let pos = el.scrollLeft;
    let stopped = false;
    let waiting = true;
    let hovered = false;
    let visible = true;

    // Mandatory snap pulls every small programmatic step back to the nearest
    // card, and a smooth scroll-behavior turns each step into its own
    // animation, so both are off while drifting and restored on stop.
    const saved = {
      snap: el.style.scrollSnapType,
      behavior: el.style.scrollBehavior,
    };
    el.style.scrollSnapType = "none";
    el.style.scrollBehavior = "auto";

    const running = () => !stopped && !waiting && !hovered && visible && !document.hidden;

    function schedule() {
      cancelAnimationFrame(raf);
      last = 0;
      if (running()) raf = requestAnimationFrame(frame);
    }

    function wait(ms: number, then: () => void) {
      waiting = true;
      cancelAnimationFrame(raf);
      timer = window.setTimeout(() => {
        waiting = false;
        then();
        schedule();
      }, ms);
    }

    function frame(now: number) {
      if (!running() || !el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 1) return;
      if (last) pos += ((now - last) / 1000) * SPEED_PX_PER_SEC;
      last = now;
      if (pos >= max) {
        el.scrollLeft = max;
        wait(END_PAUSE_MS, () => {
          el.scrollTo({ left: 0, behavior: "smooth" });
          pos = 0;
          wait(RETURN_MS + END_PAUSE_MS, () => {});
        });
        return;
      }
      el.scrollLeft = pos;
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      el!.style.scrollSnapType = saved.snap;
      el!.style.scrollBehavior = saved.behavior;
      for (const type of STOP_EVENTS) el!.removeEventListener(type, stop);
      el!.removeEventListener("pointerenter", onEnter);
      el!.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", schedule);
      observer.disconnect();
    }
    stopRef.current = stop;

    function onEnter(e: PointerEvent) {
      if (e.pointerType !== "mouse") return;
      hovered = true;
      pos = el!.scrollLeft;
      cancelAnimationFrame(raf);
    }
    function onLeave(e: PointerEvent) {
      if (e.pointerType !== "mouse") return;
      hovered = false;
      schedule();
    }

    for (const type of STOP_EVENTS) el.addEventListener(type, stop, { passive: true });
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
    document.addEventListener("visibilitychange", schedule);

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true;
      schedule();
    });
    observer.observe(el);

    wait(START_DELAY_MS, () => {
      pos = el.scrollLeft;
    });

    return stop;
  }, [ref, enabled]);

  return useCallback(() => stopRef.current(), []);
}
